import React, { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { Button, Text } from '@cubeyou-team/hx-react-components';
import { zodResolver } from '@hookform/resolvers/zod';
import { interpolate } from '@discover-frontend/utils';

import { Form } from '../../../../../../components';
import InputFormItem from '../../../../../../components/InputFormItem';
import translations from '../../../../../../lang';
import { isAbortError } from '../../../../../../utils/fetchJson';
import { useTrialStartMutation } from '../../api/hooks/useTrialStartMutation';
import { TrialStartApiError } from '../../api/request';
import {
  trialStartSchema,
  type TrialStartFormValues,
} from '../../schema/schema';
import { ReCaptchaV2, type ReCaptchaV2Handle } from '../recaptcha-v2';
import { useTrialEmailValidation } from './hooks/use-trial-email-validation';

const { TRIAL_START } = translations['en-US'];
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '';
const defaultValues: TrialStartFormValues = {
  email: '',
};

export const TrialStartContent: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCaptchaV2Handle>(null);
  const { search } = useLocation();
  const { mutateAsync: startTrial, isLoading: isSubmitting } =
    useTrialStartMutation();

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    getValues,
    getFieldState,
    trigger,
    formState: { errors, isValid },
  } = useForm<TrialStartFormValues>({
    resolver: zodResolver(trialStartSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    isEmailVerified,
    handleEmailChange,
    handleEmailBlur,
    ensureEmailValidForSubmit,
  } = useTrialEmailValidation({
    setError,
    clearErrors,
    getValues,
    getFieldState,
    trigger,
  });

  const isRecaptchaRequired = RECAPTCHA_SITE_KEY.length > 0;
  const isRecaptchaComplete = !isRecaptchaRequired || Boolean(recaptchaToken);
  const canSubmit =
    isValid && isEmailVerified && isRecaptchaComplete && !isSubmitting;

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);

    if (!(await ensureEmailValidForSubmit(values.email))) {
      return;
    }

    const recaptchaResponse =
      recaptchaRef.current?.getResponse() ?? recaptchaToken;

    if (isRecaptchaRequired && !recaptchaResponse) {
      recaptchaRef.current?.reset();
      return;
    }

    const queryString = search.replace(/^\?/, '');

    try {
      await startTrial({
        values: {
          email: values.email,
          ...(recaptchaResponse ? { recaptcha_token: recaptchaResponse } : {}),
          ...(queryString ? { query_string: queryString } : {}),
        },
      });
      setSubmittedEmail(values.email.trim());
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }

      recaptchaRef.current?.reset();

      setSubmitError(
        error instanceof TrialStartApiError
          ? error.message
          : TRIAL_START.ERRORS.START_FAILED
      );
    }
  });

  if (submittedEmail) {
    return (
      <div className="flex flex-col items-center gap-3 text-center">
        <Text variant="dark" size="xheadline">
          {TRIAL_START.SUCCESS.TITLE}
        </Text>
        <Text variant="dark" size="xbody">
          {interpolate(TRIAL_START.SUCCESS.MESSAGE, { email: submittedEmail })}
        </Text>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 text-center w-full">
      <Text variant="dark" size="xheadline" weight="semibold">
        {TRIAL_START.TITLE}
      </Text>

      <div className="flex flex-col gap-4 w-full text-left">
        <Text variant="dark" size="xbody">
          {TRIAL_START.SUBTITLE}
        </Text>

        <Form className="flex flex-col gap-4 w-full" onSubmit={onSubmit}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <InputFormItem
                label={TRIAL_START.FIELDS.EMAIL}
                type="email"
                name={field.name}
                value={field.value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleEmailChange(event, field.onChange)
                }
                onBlur={() => handleEmailBlur(field.onBlur)}
                error={Boolean(errors.email)}
                message={errors.email?.message}
                autoComplete="email"
                autoFocus
              />
            )}
          />
          <ReCaptchaV2 ref={recaptchaRef} onChange={setRecaptchaToken} />
          <div className="w-full">
            {submitError && (
              <div className="mb-2" role="alert">
                <Text tag="span" variant="error" size="small">
                  {submitError}
                </Text>
              </div>
            )}
            <Button
              className="w-full"
              variant="primary"
              disabled={!canSubmit}
              style={{ borderRadius: '0.375rem' }}
              loading={isSubmitting ? 'true' : undefined}
              type="submit"
            >
              {TRIAL_START.SUBMIT}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};
